import { useEffect, useState } from "react";
import { format } from "date-fns";
import { PuffLoader } from "react-spinners";
import {
  DevicePhoneMobileIcon,
  EnvelopeIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { CalendarEvent, CheckSquare } from "react-bootstrap-icons";
import { cn } from "@utils";
import { Banner, Button, Modal } from "@components";
import { useUsers } from "@api/usersController";
import { useCalendarItemDetail } from "../hooks/useCalendar";
import OwnerBadge from "./OwnerBadge";
import PlanDetailModal from "./PlanDetailModal";

// Match the plan/promise modal's date style exactly — abbreviated weekday + month ("Fri, 05 Sep
// 2026") and a lowercase am/pm time ("2pm" / "2:30pm") — so dates read the same across the calendar.
const formatDayTime = (date) => format(date, date.getMinutes() === 0 ? "haaa" : "h:mmaaa");

const formatWhen = (item) => {
  const anchor = item.start ?? item.due;
  if (!anchor) return "No date";
  const day = format(anchor, "EEE, dd MMM yyyy");
  if (item.allDay) return `${day} · All day`;
  const time = formatDayTime(anchor);
  const endTime = item.end ? ` – ${formatDayTime(item.end)}` : "";
  return `${day} · ${time}${endTime}`;
};

const toDate = (iso) => {
  const date = iso ? new Date(iso) : null;
  return date && !Number.isNaN(date.getTime()) ? date : null;
};

// Callers opening by id alone (e.g. a task-assigned notification passes just { uid }) have no grid
// item to read the title, kind and dates from, so fill whatever's missing from the REST detail.
const withDetail = (item, detail) => {
  if (!detail) return item;
  const hasDates = !!(item.start || item.due);
  return {
    ...item,
    kind: item.kind ?? (detail.kind === "Task" ? "task" : undefined),
    summary: item.summary || detail.title,
    ...(hasDates
      ? {}
      : {
          start: toDate(detail.startAt),
          end: toDate(detail.endAt),
          due: toDate(detail.dueAt),
          allDay: !!detail.allDay,
        }),
  };
};

const EventDetails = ({
  item: baseItem,
  isOpen,
  onClose,
  readOnly = false,
  owner = null,
  onEdit,
  onDelete,
  deleting,
  onCancelAction,
  cancelling,
  // Stack above an already-open modal (e.g. opened from the Smart Note's activity feed).
  elevated = false,
}) => {
  const { data: detail, isLoading: detailLoading } = useCalendarItemDetail(baseItem, isOpen);
  const { data: users } = useUsers();
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  useEffect(() => {
    if (!isOpen) setConfirmingDelete(false);
  }, [isOpen]);

  if (!baseItem) return null;
  const item = withDetail(baseItem, detail);

  const actionId = detail?.projectionData?.actionId;

  // Service-projected items (scheduled payment-request sends) are read-only and get the branded
  // plan-detail modal — a full instalment timeline keyed purely by the action id. A promise to pay is
  // a one-instalment plan and reads through the same modal.
  const isProjection =
    detail?.kind === "ActionProjection" || !!item.projection?.actionType;

  if (isProjection) {
    return (
      <PlanDetailModal
        actionId={actionId}
        isOpen={isOpen}
        onClose={onClose}
        owner={owner}
        onCancelAction={readOnly ? null : onCancelAction}
        cancelling={cancelling}
        focusInstalment={item.projection?.instalment ?? null}
        elevated={elevated}
        // The calendar grid item carries scheduleKind (off X-ONTIK-SCHEDULE-KIND); the plan's own
        // REST header doesn't yet, so pass it down as a hint so a merchant-scheduled send opened
        // from the calendar shows its own identity rather than reading as a promise to pay.
        scheduleKindHint={item.projection?.scheduleKind ?? null}
      />
    );
  }

  const isTask = item.kind === "task";
  const Icon = isTask ? CheckSquare : CalendarEvent;
  // Tasks carry their neutral slate identity; events lead with the signature orange.
  const tile = isTask ? "bg-slate-100 text-slate-600" : "bg-primary-50 text-primary-600";

  // The CalDAV feed redacts descriptions, so the grid item never carries the real one — read it from
  // the authenticated REST detail. Plain field: show it when there is one.
  const description = detail?.description?.trim() || null;

  // Customer info — resolved by the BFF off the item's customer ref, shown the same way the plan modal
  // shows it (name + account code, then contact channels).
  const customerRef = [detail?.customerName, detail?.customerReference && `(${detail.customerReference})`]
    .filter(Boolean)
    .join(" ");
  const email = detail?.customerEmail;
  const phone = detail?.customerPhone;

  const createdAt = detail?.createdAt ? new Date(detail.createdAt) : null;
  const createdBy = detail?.createdByUserId
    ? users?.find(
        (u) => String(u.id).toLowerCase() === String(detail.createdByUserId).toLowerCase(),
      )?.name
    : null;
  const createdLine = createdAt
    ? [`Created ${format(createdAt, "EEEE, dd MMMM yyyy 'at' HH:mm")}`, createdBy]
        .filter(Boolean)
        .join(" · ")
    : null;

  const loading = detailLoading && !detail;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={loading ? "Loading…" : isTask ? "Task" : item.summary || "(no title)"}
      titleClassName="text-xl"
      size="md"
      elevated={elevated}
    >
      <div className="flex flex-col">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <PuffLoader color="#F95816" size={64} />
          </div>
        ) : (
          <>
            <div className="space-y-4 p-5">
              <OwnerBadge owner={owner} />

              {(customerRef || email || phone) && (
                <div>
                  {customerRef && (
                    <p className="text-sm font-semibold text-zinc-900">{customerRef}</p>
                  )}
                  {(email || phone) && (
                    <div className="mt-1 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-zinc-700">
                      {email && (
                        <span className="flex items-center gap-1.5">
                          <EnvelopeIcon className="size-4 shrink-0 text-zinc-400" />
                          {email}
                        </span>
                      )}
                      {phone && (
                        <span className="flex items-center gap-1.5">
                          <DevicePhoneMobileIcon className="size-4 shrink-0 text-zinc-400" />
                          {phone}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* The identity box — the same tile + heading + summary + created line the plan modal
                  leads with, and a bin to delete (matching the plan/promise cancel affordance). */}
              <div className="flex items-center gap-3.5 rounded-2xl border border-zinc-200 p-4">
                <div className={cn("flex size-10 shrink-0 items-center justify-center rounded-md", tile)}>
                  <Icon className="size-5" />
                </div>
                <div className="min-w-0 flex-1 space-y-1">
                  <p className="text-sm font-semibold text-zinc-900">
                    {item.summary || "(no title)"}
                  </p>
                  <p className="text-sm text-zinc-600">{formatWhen(item)}</p>
                  {createdLine && <p className="text-xs text-zinc-400">{createdLine}</p>}
                </div>
                {!readOnly && !confirmingDelete && (
                  <button
                    type="button"
                    onClick={() => setConfirmingDelete(true)}
                    title={`Delete ${isTask ? "task" : "event"}`}
                    className="shrink-0 rounded-md p-1 text-red-500 hover:bg-red-50 hover:text-red-600"
                  >
                    <span className="sr-only">Delete {isTask ? "task" : "event"}</span>
                    <TrashIcon className="size-5" />
                  </button>
                )}
              </div>

              {confirmingDelete && (
                <Banner
                  tone="error"
                  message={`This ${isTask ? "task" : "event"} will be permanently deleted.`}
                  textClassName="text-xs font-semibold text-zinc-800"
                />
              )}

              {description && (
                <div className="border-t border-zinc-100 pt-4">
                  <h4 className="mb-1.5 text-xs font-medium uppercase tracking-wide text-zinc-400">
                    Description
                  </h4>
                  <p className="whitespace-pre-wrap text-sm text-zinc-600">{description}</p>
                </div>
              )}
            </div>

            {/* Edit/Delete only apply to your OWN user-authored items; a colleague's shared calendar
                is read-only (enforced at the BFF too). Delete confirms in place, like the plan modal's
                cancel — the bin (in the identity box) opens this footer. */}
            {confirmingDelete ? (
              <div className="flex shrink-0 items-center justify-between border-t border-zinc-200 p-4">
                <Button color="secondary" onClick={() => setConfirmingDelete(false)}>
                  Back
                </Button>
                <Button color="danger" onClick={onDelete} submitting={deleting}>
                  Delete {isTask ? "task" : "event"}
                </Button>
              </div>
            ) : (
              !readOnly && (
                <div className="flex shrink-0 items-center justify-end border-t border-zinc-200 bg-zinc-50/60 p-4">
                  <Button color="primary" onClick={() => onEdit?.(description ?? "")}>
                    Edit
                  </Button>
                </div>
              )
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default EventDetails;
